import { createFileRoute } from '@tanstack/react-router'
import { PageRenderer } from '@/features/core/components/PageRenderer'
import type { Page } from '@/types'

export const Route = createFileRoute('/about')({
  component: AboutPage,
  loader: async () => {
    try {
      const response = await fetch('/data/pages.json')
      const pages: Page[] = await response.json()
      const aboutPage = pages.find((p) => p.slug === 'about')
      return { page: aboutPage }
    } catch {
      return { page: null }
    }
  },
})

function AboutPage() {
  const { page } = Route.useLoaderData()

  // If we have a page from the page builder, render it
  if (page) {
    return <PageRenderer page={page} />
  }

  // Fallback to static content if no page builder content exists
  return (
    <div>
      {/* Hero */}
      <section className="bg-[var(--color-secondary)] text-white py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold mb-6">Our Story</h1>
          <p className="text-xl text-gray-300">
            Crafting quality furniture since 2010
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="prose prose-lg max-w-none">
            <h2>Who We Are</h2>
            <p>
              Premium Furniture is dedicated to bringing you the finest quality furniture for your home.
              Our pieces are designed with both aesthetics and functionality in mind, ensuring they not
              only look beautiful but also stand the test of time.
            </p>

            <h2>Our Mission</h2>
            <p>
              We believe everyone deserves a beautiful home. Our mission is to make premium furniture
              accessible while maintaining the highest standards of craftsmanship and sustainability.
            </p>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Our Team</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { name: "Raj Sharma", role: "Founder & CEO" },
              { name: "Priya Patel", role: "Head of Design" },
              { name: "Amit Kumar", role: "Master Craftsman" },
              { name: "Neha Singh", role: "Customer Experience" },
            ].map((member) => (
              <div key={member.name} className="text-center">
                <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gray-200 flex items-center justify-center text-gray-400 text-2xl">
                  👤
                </div>
                <div className="font-semibold">{member.name}</div>
                <div className="text-sm text-gray-500">{member.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "15+", label: "Years Experience" },
              { value: "50K+", label: "Happy Customers" },
              { value: "1000+", label: "Products Crafted" },
              { value: "100%", label: "Sustainable" },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-4xl font-bold text-[var(--color-primary)]">{stat.value}</div>
                <div className="text-gray-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
